import type { OpCodeDef } from './types';

const OpCodes = {
  "unreachable": {
    value: 0,
    mnemonic: "unreachable",
    stackBehavior: "None",
  },
  "nop": {
    value: 1,
    mnemonic: "nop",
    stackBehavior: "None",
  },
  "block": {
    value: 2,
    mnemonic: "block",
    immediate: "BlockSignature",
    controlFlow: "Push",
    stackBehavior: "None",
  },
  "loop": {
    value: 3,
    mnemonic: "loop",
    immediate: "BlockSignature",
    controlFlow: "Push",
    stackBehavior: "None",
  },
  "if": {
    value: 4,
    mnemonic: "if",
    immediate: "BlockSignature",
    controlFlow: "Push",
    stackBehavior: "Pop",
    popOperands: ["Int32"] as const,
  },
  "else": {
    value: 5,
    mnemonic: "else",
    stackBehavior: "None",
  },
  "try": {
    value: 6,
    mnemonic: "try",
    immediate: "BlockSignature",
    controlFlow: "Push",
    stackBehavior: "None",
    feature: "exception-handling",
  },
  "catch": {
    value: 7,
    mnemonic: "catch",
    immediate: "VarUInt32",
    stackBehavior: "None",
    feature: "exception-handling",
  },
  "throw": {
    value: 8,
    mnemonic: "throw",
    immediate: "VarUInt32",
    stackBehavior: "None",
    feature: "exception-handling",
  },
  "rethrow": {
    value: 9,
    mnemonic: "rethrow",
    immediate: "VarUInt32",
    stackBehavior: "None",
    feature: "exception-handling",
  },
  "end": {
    value: 11,
    mnemonic: "end",
    controlFlow: "Pop",
    stackBehavior: "None",
  },
  "br": {
    value: 12,
    mnemonic: "br",
    immediate: "RelativeDepth",
    stackBehavior: "None",
  },
  "br_if": {
    value: 13,
    mnemonic: "br_if",
    immediate: "RelativeDepth",
    stackBehavior: "Pop",
    popOperands: ["Int32"] as const,
  },
  "br_table": {
    value: 14,
    mnemonic: "br_table",
    immediate: "BranchTable",
    stackBehavior: "Pop",
    popOperands: ["Int32"] as const,
  },
  "return": {
    value: 15,
    mnemonic: "return",
    stackBehavior: "None",
  },
  "call": {
    value: 16,
    mnemonic: "call",
    immediate: "Function",
    stackBehavior: "PopPush",
  },
  "call_indirect": {
    value: 17,
    mnemonic: "call_indirect",
    immediate: "IndirectFunction",
    stackBehavior: "PopPush",
    popOperands: ["Int32"] as const,
  },
  "return_call": {
    value: 18,
    mnemonic: "return_call",
    immediate: "Function",
    stackBehavior: "PopPush",
    feature: "tail-call",
  },
  "return_call_indirect": {
    value: 19,
    mnemonic: "return_call_indirect",
    immediate: "IndirectFunction",
    stackBehavior: "PopPush",
    popOperands: ["Int32"] as const,
    feature: "tail-call",
  },
  "delegate": {
    value: 24,
    mnemonic: "delegate",
    immediate: "VarUInt32",
    stackBehavior: "None",
    feature: "exception-handling",
  },
  "catch_all": {
    value: 25,
    mnemonic: "catch_all",
    stackBehavior: "None",
    feature: "exception-handling",
  },
  "drop": {
    value: 26,
    mnemonic: "drop",
    stackBehavior: "Pop",
    popOperands: ["Any"] as const,
  },
  "select": {
    value: 27,
    mnemonic: "select",
    stackBehavior: "PopPush",
    popOperands: ["Any","Any","Int32"] as const,
    pushOperands: ["Any"] as const,
  },
  "get_local": {
    value: 32,
    mnemonic: "local.get",
    immediate: "Local",
    stackBehavior: "Push",
    pushOperands: ["Any"] as const,
  },
  "set_local": {
    value: 33,
    mnemonic: "local.set",
    immediate: "Local",
    stackBehavior: "Pop",
    popOperands: ["Any"] as const,
  },
  "tee_local": {
    value: 34,
    mnemonic: "local.tee",
    immediate: "Local",
    stackBehavior: "PopPush",
    popOperands: ["Any"] as const,
    pushOperands: ["Any"] as const,
  },
  "get_global": {
    value: 35,
    mnemonic: "global.get",
    immediate: "Global",
    stackBehavior: "Push",
    pushOperands: ["Any"] as const,
  },
  "set_global": {
    value: 36,
    mnemonic: "global.set",
    immediate: "Global",
    stackBehavior: "Pop",
    popOperands: ["Any"] as const,
  },
  "i32_load": {
    value: 40,
    mnemonic: "i32.load",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32"] as const,
    pushOperands: ["Int32"] as const,
  },
  "i64_load": {
    value: 41,
    mnemonic: "i64.load",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32"] as const,
    pushOperands: ["Int64"] as const,
  },
  "f32_load": {
    value: 42,
    mnemonic: "f32.load",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32"] as const,
    pushOperands: ["Float32"] as const,
  },
  "f64_load": {
    value: 43,
    mnemonic: "f64.load",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32"] as const,
    pushOperands: ["Float64"] as const,
  },
  "i32_load8_s": {
    value: 44,
    mnemonic: "i32.load8_s",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32"] as const,
    pushOperands: ["Int32"] as const,
  },
  "i32_load8_u": {
    value: 45,
    mnemonic: "i32.load8_u",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32"] as const,
    pushOperands: ["Int32"] as const,
  },
  "i32_load16_s": {
    value: 46,
    mnemonic: "i32.load16_s",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32"] as const,
    pushOperands: ["Int32"] as const,
  },
  "i32_load16_u": {
    value: 47,
    mnemonic: "i32.load16_u",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32"] as const,
    pushOperands: ["Int32"] as const,
  },
  "i64_load8_s": {
    value: 48,
    mnemonic: "i64.load8_s",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32"] as const,
    pushOperands: ["Int64"] as const,
  },
  "i64_load8_u": {
    value: 49,
    mnemonic: "i64.load8_u",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32"] as const,
    pushOperands: ["Int64"] as const,
  },
  "i64_load16_s": {
    value: 50,
    mnemonic: "i64.load16_s",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32"] as const,
    pushOperands: ["Int64"] as const,
  },
  "i64_load16_u": {
    value: 51,
    mnemonic: "i64.load16_u",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32"] as const,
    pushOperands: ["Int64"] as const,
  },
  "i64_load32_s": {
    value: 52,
    mnemonic: "i64.load32_s",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32"] as const,
    pushOperands: ["Int64"] as const,
  },
  "i64_load32_u": {
    value: 53,
    mnemonic: "i64.load32_u",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32"] as const,
    pushOperands: ["Int64"] as const,
  },
  "i32_store": {
    value: 54,
    mnemonic: "i32.store",
    immediate: "MemoryImmediate",
    stackBehavior: "Pop",
    popOperands: ["Int32","Int32"] as const,
  },
  "i64_store": {
    value: 55,
    mnemonic: "i64.store",
    immediate: "MemoryImmediate",
    stackBehavior: "Pop",
    popOperands: ["Int32","Int64"] as const,
  },
  "f32_store": {
    value: 56,
    mnemonic: "f32.store",
    immediate: "MemoryImmediate",
    stackBehavior: "Pop",
    popOperands: ["Int32","Float32"] as const,
  },
  "f64_store": {
    value: 57,
    mnemonic: "f64.store",
    immediate: "MemoryImmediate",
    stackBehavior: "Pop",
    popOperands: ["Int32","Float64"] as const,
  },
  "i32_store8": {
    value: 58,
    mnemonic: "i32.store8",
    immediate: "MemoryImmediate",
    stackBehavior: "Pop",
    popOperands: ["Int32","Int32"] as const,
  },
  "i32_store16": {
    value: 59,
    mnemonic: "i32.store16",
    immediate: "MemoryImmediate",
    stackBehavior: "Pop",
    popOperands: ["Int32","Int32"] as const,
  },
  "i64_store8": {
    value: 60,
    mnemonic: "i64.store8",
    immediate: "MemoryImmediate",
    stackBehavior: "Pop",
    popOperands: ["Int32","Int64"] as const,
  },
  "i64_store16": {
    value: 61,
    mnemonic: "i64.store16",
    immediate: "MemoryImmediate",
    stackBehavior: "Pop",
    popOperands: ["Int32","Int64"] as const,
  },
  "i64_store32": {
    value: 62,
    mnemonic: "i64.store32",
    immediate: "MemoryImmediate",
    stackBehavior: "Pop",
    popOperands: ["Int32","Int64"] as const,
  },
  "mem_size": {
    value: 63,
    mnemonic: "memory.size",
    immediate: "VarUInt1",
    stackBehavior: "Push",
    pushOperands: ["Int32"] as const,
  },
  "mem_grow": {
    value: 64,
    mnemonic: "memory.grow",
    immediate: "VarUInt1",
    stackBehavior: "PopPush",
    popOperands: ["Int32"] as const,
    pushOperands: ["Int32"] as const,
  },
  "i32_const": {
    value: 65,
    mnemonic: "i32.const",
    immediate: "VarInt32",
    stackBehavior: "Push",
    pushOperands: ["Int32"] as const,
  },
  "i64_const": {
    value: 66,
    mnemonic: "i64.const",
    immediate: "VarInt64",
    stackBehavior: "Push",
    pushOperands: ["Int64"] as const,
  },
  "f32_const": {
    value: 67,
    mnemonic: "f32.const",
    immediate: "Float32",
    stackBehavior: "Push",
    pushOperands: ["Float32"] as const,
  },
  "f64_const": {
    value: 68,
    mnemonic: "f64.const",
    immediate: "Float64",
    stackBehavior: "Push",
    pushOperands: ["Float64"] as const,
  },
  "i32_eqz": {
    value: 69,
    mnemonic: "i32.eqz",
    stackBehavior: "PopPush",
    popOperands: ["Int32"] as const,
    pushOperands: ["Int32"] as const,
  },
  "i32_eq": {
    value: 70,
    mnemonic: "i32.eq",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int32"] as const,
    pushOperands: ["Int32"] as const,
  },
  "i32_ne": {
    value: 71,
    mnemonic: "i32.ne",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int32"] as const,
    pushOperands: ["Int32"] as const,
  },
  "i32_lt_s": {
    value: 72,
    mnemonic: "i32.lt_s",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int32"] as const,
    pushOperands: ["Int32"] as const,
  },
  "i32_lt_u": {
    value: 73,
    mnemonic: "i32.lt_u",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int32"] as const,
    pushOperands: ["Int32"] as const,
  },
  "i32_gt_s": {
    value: 74,
    mnemonic: "i32.gt_s",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int32"] as const,
    pushOperands: ["Int32"] as const,
  },
  "i32_gt_u": {
    value: 75,
    mnemonic: "i32.gt_u",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int32"] as const,
    pushOperands: ["Int32"] as const,
  },
  "i32_le_s": {
    value: 76,
    mnemonic: "i32.le_s",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int32"] as const,
    pushOperands: ["Int32"] as const,
  },
  "i32_le_u": {
    value: 77,
    mnemonic: "i32.le_u",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int32"] as const,
    pushOperands: ["Int32"] as const,
  },
  "i32_ge_s": {
    value: 78,
    mnemonic: "i32.ge_s",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int32"] as const,
    pushOperands: ["Int32"] as const,
  },
  "i32_ge_u": {
    value: 79,
    mnemonic: "i32.ge_u",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int32"] as const,
    pushOperands: ["Int32"] as const,
  },
  "i64_eqz": {
    value: 80,
    mnemonic: "i64.eqz",
    stackBehavior: "PopPush",
    popOperands: ["Int64"] as const,
    pushOperands: ["Int32"] as const,
  },
  "i64_eq": {
    value: 81,
    mnemonic: "i64.eq",
    stackBehavior: "PopPush",
    popOperands: ["Int64","Int64"] as const,
    pushOperands: ["Int32"] as const,
  },
  "i64_ne": {
    value: 82,
    mnemonic: "i64.ne",
    stackBehavior: "PopPush",
    popOperands: ["Int64","Int64"] as const,
    pushOperands: ["Int32"] as const,
  },
  "i64_lt_s": {
    value: 83,
    mnemonic: "i64.lt_s",
    stackBehavior: "PopPush",
    popOperands: ["Int64","Int64"] as const,
    pushOperands: ["Int32"] as const,
  },
  "i64_lt_u": {
    value: 84,
    mnemonic: "i64.lt_u",
    stackBehavior: "PopPush",
    popOperands: ["Int64","Int64"] as const,
    pushOperands: ["Int32"] as const,
  },
  "i64_gt_s": {
    value: 85,
    mnemonic: "i64.gt_s",
    stackBehavior: "PopPush",
    popOperands: ["Int64","Int64"] as const,
    pushOperands: ["Int32"] as const,
  },
  "i64_gt_u": {
    value: 86,
    mnemonic: "i64.gt_u",
    stackBehavior: "PopPush",
    popOperands: ["Int64","Int64"] as const,
    pushOperands: ["Int32"] as const,
  },
  "i64_le_s": {
    value: 87,
    mnemonic: "i64.le_s",
    stackBehavior: "PopPush",
    popOperands: ["Int64","Int64"] as const,
    pushOperands: ["Int32"] as const,
  },
  "i64_le_u": {
    value: 88,
    mnemonic: "i64.le_u",
    stackBehavior: "PopPush",
    popOperands: ["Int64","Int64"] as const,
    pushOperands: ["Int32"] as const,
  },
  "i64_ge_s": {
    value: 89,
    mnemonic: "i64.ge_s",
    stackBehavior: "PopPush",
    popOperands: ["Int64","Int64"] as const,
    pushOperands: ["Int32"] as const,
  },
  "i64_ge_u": {
    value: 90,
    mnemonic: "i64.ge_u",
    stackBehavior: "PopPush",
    popOperands: ["Int64","Int64"] as const,
    pushOperands: ["Int32"] as const,
  },
  "f32_eq": {
    value: 91,
    mnemonic: "f32.eq",
    stackBehavior: "PopPush",
    popOperands: ["Float32","Float32"] as const,
    pushOperands: ["Int32"] as const,
  },
  "f32_ne": {
    value: 92,
    mnemonic: "f32.ne",
    stackBehavior: "PopPush",
    popOperands: ["Float32","Float32"] as const,
    pushOperands: ["Int32"] as const,
  },
  "f32_lt": {
    value: 93,
    mnemonic: "f32.lt",
    stackBehavior: "PopPush",
    popOperands: ["Float32","Float32"] as const,
    pushOperands: ["Int32"] as const,
  },
  "f32_gt": {
    value: 94,
    mnemonic: "f32.gt",
    stackBehavior: "PopPush",
    popOperands: ["Float32","Float32"] as const,
    pushOperands: ["Int32"] as const,
  },
  "f32_le": {
    value: 95,
    mnemonic: "f32.le",
    stackBehavior: "PopPush",
    popOperands: ["Float32","Float32"] as const,
    pushOperands: ["Int32"] as const,
  },
  "f32_ge": {
    value: 96,
    mnemonic: "f32.ge",
    stackBehavior: "PopPush",
    popOperands: ["Float32","Float32"] as const,
    pushOperands: ["Int32"] as const,
  },
  "f64_eq": {
    value: 97,
    mnemonic: "f64.eq",
    stackBehavior: "PopPush",
    popOperands: ["Float64","Float64"] as const,
    pushOperands: ["Int32"] as const,
  },
  "f64_ne": {
    value: 98,
    mnemonic: "f64.ne",
    stackBehavior: "PopPush",
    popOperands: ["Float64","Float64"] as const,
    pushOperands: ["Int32"] as const,
  },
  "f64_lt": {
    value: 99,
    mnemonic: "f64.lt",
    stackBehavior: "PopPush",
    popOperands: ["Float64","Float64"] as const,
    pushOperands: ["Int32"] as const,
  },
  "f64_gt": {
    value: 100,
    mnemonic: "f64.gt",
    stackBehavior: "PopPush",
    popOperands: ["Float64","Float64"] as const,
    pushOperands: ["Int32"] as const,
  },
  "f64_le": {
    value: 101,
    mnemonic: "f64.le",
    stackBehavior: "PopPush",
    popOperands: ["Float64","Float64"] as const,
    pushOperands: ["Int32"] as const,
  },
  "f64_ge": {
    value: 102,
    mnemonic: "f64.ge",
    stackBehavior: "PopPush",
    popOperands: ["Float64","Float64"] as const,
    pushOperands: ["Int32"] as const,
  },
  "i32_clz": {
    value: 103,
    mnemonic: "i32.clz",
    stackBehavior: "PopPush",
    popOperands: ["Int32"] as const,
    pushOperands: ["Int32"] as const,
  },
  "i32_ctz": {
    value: 104,
    mnemonic: "i32.ctz",
    stackBehavior: "PopPush",
    popOperands: ["Int32"] as const,
    pushOperands: ["Int32"] as const,
  },
  "i32_popcnt": {
    value: 105,
    mnemonic: "i32.popcnt",
    stackBehavior: "PopPush",
    popOperands: ["Int32"] as const,
    pushOperands: ["Int32"] as const,
  },
  "i32_add": {
    value: 106,
    mnemonic: "i32.add",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int32"] as const,
    pushOperands: ["Int32"] as const,
  },
  "i32_sub": {
    value: 107,
    mnemonic: "i32.sub",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int32"] as const,
    pushOperands: ["Int32"] as const,
  },
  "i32_mul": {
    value: 108,
    mnemonic: "i32.mul",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int32"] as const,
    pushOperands: ["Int32"] as const,
  },
  "i32_div_s": {
    value: 109,
    mnemonic: "i32.div_s",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int32"] as const,
    pushOperands: ["Int32"] as const,
  },
  "i32_div_u": {
    value: 110,
    mnemonic: "i32.div_u",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int32"] as const,
    pushOperands: ["Int32"] as const,
  },
  "i32_rem_s": {
    value: 111,
    mnemonic: "i32.rem_s",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int32"] as const,
    pushOperands: ["Int32"] as const,
  },
  "i32_rem_u": {
    value: 112,
    mnemonic: "i32.rem_u",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int32"] as const,
    pushOperands: ["Int32"] as const,
  },
  "i32_and": {
    value: 113,
    mnemonic: "i32.and",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int32"] as const,
    pushOperands: ["Int32"] as const,
  },
  "i32_or": {
    value: 114,
    mnemonic: "i32.or",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int32"] as const,
    pushOperands: ["Int32"] as const,
  },
  "i32_xor": {
    value: 115,
    mnemonic: "i32.xor",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int32"] as const,
    pushOperands: ["Int32"] as const,
  },
  "i32_shl": {
    value: 116,
    mnemonic: "i32.shl",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int32"] as const,
    pushOperands: ["Int32"] as const,
  },
  "i32_shr_s": {
    value: 117,
    mnemonic: "i32.shr_s",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int32"] as const,
    pushOperands: ["Int32"] as const,
  },
  "i32_shr_u": {
    value: 118,
    mnemonic: "i32.shr_u",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int32"] as const,
    pushOperands: ["Int32"] as const,
  },
  "i32_rotl": {
    value: 119,
    mnemonic: "i32.rotl",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int32"] as const,
    pushOperands: ["Int32"] as const,
  },
  "i32_rotr": {
    value: 120,
    mnemonic: "i32.rotr",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int32"] as const,
    pushOperands: ["Int32"] as const,
  },
  "i64_clz": {
    value: 121,
    mnemonic: "i64.clz",
    stackBehavior: "PopPush",
    popOperands: ["Int64"] as const,
    pushOperands: ["Int64"] as const,
  },
  "i64_ctz": {
    value: 122,
    mnemonic: "i64.ctz",
    stackBehavior: "PopPush",
    popOperands: ["Int64"] as const,
    pushOperands: ["Int64"] as const,
  },
  "i64_popcnt": {
    value: 123,
    mnemonic: "i64.popcnt",
    stackBehavior: "PopPush",
    popOperands: ["Int64"] as const,
    pushOperands: ["Int64"] as const,
  },
  "i64_add": {
    value: 124,
    mnemonic: "i64.add",
    stackBehavior: "PopPush",
    popOperands: ["Int64","Int64"] as const,
    pushOperands: ["Int64"] as const,
  },
  "i64_sub": {
    value: 125,
    mnemonic: "i64.sub",
    stackBehavior: "PopPush",
    popOperands: ["Int64","Int64"] as const,
    pushOperands: ["Int64"] as const,
  },
  "i64_mul": {
    value: 126,
    mnemonic: "i64.mul",
    stackBehavior: "PopPush",
    popOperands: ["Int64","Int64"] as const,
    pushOperands: ["Int64"] as const,
  },
  "i64_div_s": {
    value: 127,
    mnemonic: "i64.div_s",
    stackBehavior: "PopPush",
    popOperands: ["Int64","Int64"] as const,
    pushOperands: ["Int64"] as const,
  },
  "i64_div_u": {
    value: 128,
    mnemonic: "i64.div_u",
    stackBehavior: "PopPush",
    popOperands: ["Int64","Int64"] as const,
    pushOperands: ["Int64"] as const,
  },
  "i64_rem_s": {
    value: 129,
    mnemonic: "i64.rem_s",
    stackBehavior: "PopPush",
    popOperands: ["Int64","Int64"] as const,
    pushOperands: ["Int64"] as const,
  },
  "i64_rem_u": {
    value: 130,
    mnemonic: "i64.rem_u",
    stackBehavior: "PopPush",
    popOperands: ["Int64","Int64"] as const,
    pushOperands: ["Int64"] as const,
  },
  "i64_and": {
    value: 131,
    mnemonic: "i64.and",
    stackBehavior: "PopPush",
    popOperands: ["Int64","Int64"] as const,
    pushOperands: ["Int64"] as const,
  },
  "i64_or": {
    value: 132,
    mnemonic: "i64.or",
    stackBehavior: "PopPush",
    popOperands: ["Int64","Int64"] as const,
    pushOperands: ["Int64"] as const,
  },
  "i64_xor": {
    value: 133,
    mnemonic: "i64.xor",
    stackBehavior: "PopPush",
    popOperands: ["Int64","Int64"] as const,
    pushOperands: ["Int64"] as const,
  },
  "i64_shl": {
    value: 134,
    mnemonic: "i64.shl",
    stackBehavior: "PopPush",
    popOperands: ["Int64","Int64"] as const,
    pushOperands: ["Int64"] as const,
  },
  "i64_shr_s": {
    value: 135,
    mnemonic: "i64.shr_s",
    stackBehavior: "PopPush",
    popOperands: ["Int64","Int64"] as const,
    pushOperands: ["Int64"] as const,
  },
  "i64_shr_u": {
    value: 136,
    mnemonic: "i64.shr_u",
    stackBehavior: "PopPush",
    popOperands: ["Int64","Int64"] as const,
    pushOperands: ["Int64"] as const,
  },
  "i64_rotl": {
    value: 137,
    mnemonic: "i64.rotl",
    stackBehavior: "PopPush",
    popOperands: ["Int64","Int64"] as const,
    pushOperands: ["Int64"] as const,
  },
  "i64_rotr": {
    value: 138,
    mnemonic: "i64.rotr",
    stackBehavior: "PopPush",
    popOperands: ["Int64","Int64"] as const,
    pushOperands: ["Int64"] as const,
  },
  "f32_abs": {
    value: 139,
    mnemonic: "f32.abs",
    stackBehavior: "PopPush",
    popOperands: ["Float32"] as const,
    pushOperands: ["Float32"] as const,
  },
  "f32_neg": {
    value: 140,
    mnemonic: "f32.neg",
    stackBehavior: "PopPush",
    popOperands: ["Float32"] as const,
    pushOperands: ["Float32"] as const,
  },
  "f32_ceil": {
    value: 141,
    mnemonic: "f32.ceil",
    stackBehavior: "PopPush",
    popOperands: ["Float32"] as const,
    pushOperands: ["Float32"] as const,
  },
  "f32_floor": {
    value: 142,
    mnemonic: "f32.floor",
    stackBehavior: "PopPush",
    popOperands: ["Float32"] as const,
    pushOperands: ["Float32"] as const,
  },
  "f32_trunc": {
    value: 143,
    mnemonic: "f32.trunc",
    stackBehavior: "PopPush",
    popOperands: ["Float32"] as const,
    pushOperands: ["Float32"] as const,
  },
  "f32_nearest": {
    value: 144,
    mnemonic: "f32.nearest",
    stackBehavior: "PopPush",
    popOperands: ["Float32"] as const,
    pushOperands: ["Float32"] as const,
  },
  "f32_sqrt": {
    value: 145,
    mnemonic: "f32.sqrt",
    stackBehavior: "PopPush",
    popOperands: ["Float32"] as const,
    pushOperands: ["Float32"] as const,
  },
  "f32_add": {
    value: 146,
    mnemonic: "f32.add",
    stackBehavior: "PopPush",
    popOperands: ["Float32","Float32"] as const,
    pushOperands: ["Float32"] as const,
  },
  "f32_sub": {
    value: 147,
    mnemonic: "f32.sub",
    stackBehavior: "PopPush",
    popOperands: ["Float32","Float32"] as const,
    pushOperands: ["Float32"] as const,
  },
  "f32_mul": {
    value: 148,
    mnemonic: "f32.mul",
    stackBehavior: "PopPush",
    popOperands: ["Float32","Float32"] as const,
    pushOperands: ["Float32"] as const,
  },
  "f32_div": {
    value: 149,
    mnemonic: "f32.div",
    stackBehavior: "PopPush",
    popOperands: ["Float32","Float32"] as const,
    pushOperands: ["Float32"] as const,
  },
  "f32_min": {
    value: 150,
    mnemonic: "f32.min",
    stackBehavior: "PopPush",
    popOperands: ["Float32","Float32"] as const,
    pushOperands: ["Float32"] as const,
  },
  "f32_max": {
    value: 151,
    mnemonic: "f32.max",
    stackBehavior: "PopPush",
    popOperands: ["Float32","Float32"] as const,
    pushOperands: ["Float32"] as const,
  },
  "f32_copysign": {
    value: 152,
    mnemonic: "f32.copysign",
    stackBehavior: "PopPush",
    popOperands: ["Float32","Float32"] as const,
    pushOperands: ["Float32"] as const,
  },
  "f64_abs": {
    value: 153,
    mnemonic: "f64.abs",
    stackBehavior: "PopPush",
    popOperands: ["Float64"] as const,
    pushOperands: ["Float64"] as const,
  },
  "f64_neg": {
    value: 154,
    mnemonic: "f64.neg",
    stackBehavior: "PopPush",
    popOperands: ["Float64"] as const,
    pushOperands: ["Float64"] as const,
  },
  "f64_ceil": {
    value: 155,
    mnemonic: "f64.ceil",
    stackBehavior: "PopPush",
    popOperands: ["Float64"] as const,
    pushOperands: ["Float64"] as const,
  },
  "f64_floor": {
    value: 156,
    mnemonic: "f64.floor",
    stackBehavior: "PopPush",
    popOperands: ["Float64"] as const,
    pushOperands: ["Float64"] as const,
  },
  "f64_trunc": {
    value: 157,
    mnemonic: "f64.trunc",
    stackBehavior: "PopPush",
    popOperands: ["Float64"] as const,
    pushOperands: ["Float64"] as const,
  },
  "f64_nearest": {
    value: 158,
    mnemonic: "f64.nearest",
    stackBehavior: "PopPush",
    popOperands: ["Float64"] as const,
    pushOperands: ["Float64"] as const,
  },
  "f64_sqrt": {
    value: 159,
    mnemonic: "f64.sqrt",
    stackBehavior: "PopPush",
    popOperands: ["Float64"] as const,
    pushOperands: ["Float64"] as const,
  },
  "f64_add": {
    value: 160,
    mnemonic: "f64.add",
    stackBehavior: "PopPush",
    popOperands: ["Float64","Float64"] as const,
    pushOperands: ["Float64"] as const,
  },
  "f64_sub": {
    value: 161,
    mnemonic: "f64.sub",
    stackBehavior: "PopPush",
    popOperands: ["Float64","Float64"] as const,
    pushOperands: ["Float64"] as const,
  },
  "f64_mul": {
    value: 162,
    mnemonic: "f64.mul",
    stackBehavior: "PopPush",
    popOperands: ["Float64","Float64"] as const,
    pushOperands: ["Float64"] as const,
  },
  "f64_div": {
    value: 163,
    mnemonic: "f64.div",
    stackBehavior: "PopPush",
    popOperands: ["Float64","Float64"] as const,
    pushOperands: ["Float64"] as const,
  },
  "f64_min": {
    value: 164,
    mnemonic: "f64.min",
    stackBehavior: "PopPush",
    popOperands: ["Float64","Float64"] as const,
    pushOperands: ["Float64"] as const,
  },
  "f64_max": {
    value: 165,
    mnemonic: "f64.max",
    stackBehavior: "PopPush",
    popOperands: ["Float64","Float64"] as const,
    pushOperands: ["Float64"] as const,
  },
  "f64_copysign": {
    value: 166,
    mnemonic: "f64.copysign",
    stackBehavior: "PopPush",
    popOperands: ["Float64","Float64"] as const,
    pushOperands: ["Float64"] as const,
  },
  "i32_wrap_i64": {
    value: 167,
    mnemonic: "i32.wrap_i64",
    stackBehavior: "PopPush",
    popOperands: ["Int64"] as const,
    pushOperands: ["Int32"] as const,
  },
  "i32_trunc_f32_s": {
    value: 168,
    mnemonic: "i32.trunc_f32_s",
    stackBehavior: "PopPush",
    popOperands: ["Float32"] as const,
    pushOperands: ["Int32"] as const,
  },
  "i32_trunc_f32_u": {
    value: 169,
    mnemonic: "i32.trunc_f32_u",
    stackBehavior: "PopPush",
    popOperands: ["Float32"] as const,
    pushOperands: ["Int32"] as const,
  },
  "i32_trunc_f64_s": {
    value: 170,
    mnemonic: "i32.trunc_f64_s",
    stackBehavior: "PopPush",
    popOperands: ["Float64"] as const,
    pushOperands: ["Int32"] as const,
  },
  "i32_trunc_f64_u": {
    value: 171,
    mnemonic: "i32.trunc_f64_u",
    stackBehavior: "PopPush",
    popOperands: ["Float64"] as const,
    pushOperands: ["Int32"] as const,
  },
  "i64_extend_i32_s": {
    value: 172,
    mnemonic: "i64.extend_i32_s",
    stackBehavior: "PopPush",
    popOperands: ["Int32"] as const,
    pushOperands: ["Int64"] as const,
  },
  "i64_extend_i32_u": {
    value: 173,
    mnemonic: "i64.extend_i32_u",
    stackBehavior: "PopPush",
    popOperands: ["Int32"] as const,
    pushOperands: ["Int64"] as const,
  },
  "i64_trunc_f32_s": {
    value: 174,
    mnemonic: "i64.trunc_f32_s",
    stackBehavior: "PopPush",
    popOperands: ["Float32"] as const,
    pushOperands: ["Int64"] as const,
  },
  "i64_trunc_f32_u": {
    value: 175,
    mnemonic: "i64.trunc_f32_u",
    stackBehavior: "PopPush",
    popOperands: ["Float32"] as const,
    pushOperands: ["Int64"] as const,
  },
  "i64_trunc_f64_s": {
    value: 176,
    mnemonic: "i64.trunc_f64_s",
    stackBehavior: "PopPush",
    popOperands: ["Float64"] as const,
    pushOperands: ["Int64"] as const,
  },
  "i64_trunc_f64_u": {
    value: 177,
    mnemonic: "i64.trunc_f64_u",
    stackBehavior: "PopPush",
    popOperands: ["Float64"] as const,
    pushOperands: ["Int64"] as const,
  },
  "f32_convert_i32_s": {
    value: 178,
    mnemonic: "f32.convert_i32_s",
    stackBehavior: "PopPush",
    popOperands: ["Int32"] as const,
    pushOperands: ["Float32"] as const,
  },
  "f32_convert_i32_u": {
    value: 179,
    mnemonic: "f32.convert_i32_u",
    stackBehavior: "PopPush",
    popOperands: ["Int32"] as const,
    pushOperands: ["Float32"] as const,
  },
  "f32_convert_i64_s": {
    value: 180,
    mnemonic: "f32.convert_i64_s",
    stackBehavior: "PopPush",
    popOperands: ["Int64"] as const,
    pushOperands: ["Float32"] as const,
  },
  "f32_convert_i64_u": {
    value: 181,
    mnemonic: "f32.convert_i64_u",
    stackBehavior: "PopPush",
    popOperands: ["Int64"] as const,
    pushOperands: ["Float32"] as const,
  },
  "f32_demote_f64": {
    value: 182,
    mnemonic: "f32.demote_f64",
    stackBehavior: "PopPush",
    popOperands: ["Float64"] as const,
    pushOperands: ["Float32"] as const,
  },
  "f64_convert_i32_s": {
    value: 183,
    mnemonic: "f64.convert_i32_s",
    stackBehavior: "PopPush",
    popOperands: ["Int32"] as const,
    pushOperands: ["Float64"] as const,
  },
  "f64_convert_i32_u": {
    value: 184,
    mnemonic: "f64.convert_i32_u",
    stackBehavior: "PopPush",
    popOperands: ["Int32"] as const,
    pushOperands: ["Float64"] as const,
  },
  "f64_convert_i64_s": {
    value: 185,
    mnemonic: "f64.convert_i64_s",
    stackBehavior: "PopPush",
    popOperands: ["Int64"] as const,
    pushOperands: ["Float64"] as const,
  },
  "f64_convert_i64_u": {
    value: 186,
    mnemonic: "f64.convert_i64_u",
    stackBehavior: "PopPush",
    popOperands: ["Int64"] as const,
    pushOperands: ["Float64"] as const,
  },
  "f64_promote_f32": {
    value: 187,
    mnemonic: "f64.promote_f32",
    stackBehavior: "PopPush",
    popOperands: ["Float32"] as const,
    pushOperands: ["Float64"] as const,
  },
  "i32_reinterpret_f32": {
    value: 188,
    mnemonic: "i32.reinterpret_f32",
    stackBehavior: "PopPush",
    popOperands: ["Float32"] as const,
    pushOperands: ["Int32"] as const,
  },
  "i64_reinterpret_f64": {
    value: 189,
    mnemonic: "i64.reinterpret_f64",
    stackBehavior: "PopPush",
    popOperands: ["Float64"] as const,
    pushOperands: ["Int64"] as const,
  },
  "f32_reinterpret_i32": {
    value: 190,
    mnemonic: "f32.reinterpret_i32",
    stackBehavior: "PopPush",
    popOperands: ["Int32"] as const,
    pushOperands: ["Float32"] as const,
  },
  "f64_reinterpret_i64": {
    value: 191,
    mnemonic: "f64.reinterpret_i64",
    stackBehavior: "PopPush",
    popOperands: ["Int64"] as const,
    pushOperands: ["Float64"] as const,
  },
  "i32_extend8_s": {
    value: 192,
    mnemonic: "i32.extend8_s",
    stackBehavior: "PopPush",
    popOperands: ["Int32"] as const,
    pushOperands: ["Int32"] as const,
    feature: "sign-extend",
  },
  "i32_extend16_s": {
    value: 193,
    mnemonic: "i32.extend16_s",
    stackBehavior: "PopPush",
    popOperands: ["Int32"] as const,
    pushOperands: ["Int32"] as const,
    feature: "sign-extend",
  },
  "i64_extend8_s": {
    value: 194,
    mnemonic: "i64.extend8_s",
    stackBehavior: "PopPush",
    popOperands: ["Int64"] as const,
    pushOperands: ["Int64"] as const,
    feature: "sign-extend",
  },
  "i64_extend16_s": {
    value: 195,
    mnemonic: "i64.extend16_s",
    stackBehavior: "PopPush",
    popOperands: ["Int64"] as const,
    pushOperands: ["Int64"] as const,
    feature: "sign-extend",
  },
  "i64_extend32_s": {
    value: 196,
    mnemonic: "i64.extend32_s",
    stackBehavior: "PopPush",
    popOperands: ["Int64"] as const,
    pushOperands: ["Int64"] as const,
    feature: "sign-extend",
  },
  "i32_trunc_sat_f32_s": {
    value: 0,
    mnemonic: "i32.trunc_sat_f32_s",
    stackBehavior: "PopPush",
    popOperands: ["Float32"] as const,
    pushOperands: ["Int32"] as const,
    prefix: 252,
    feature: "sat-trunc",
  },
  "i32_trunc_sat_f32_u": {
    value: 1,
    mnemonic: "i32.trunc_sat_f32_u",
    stackBehavior: "PopPush",
    popOperands: ["Float32"] as const,
    pushOperands: ["Int32"] as const,
    prefix: 252,
    feature: "sat-trunc",
  },
  "i32_trunc_sat_f64_s": {
    value: 2,
    mnemonic: "i32.trunc_sat_f64_s",
    stackBehavior: "PopPush",
    popOperands: ["Float64"] as const,
    pushOperands: ["Int32"] as const,
    prefix: 252,
    feature: "sat-trunc",
  },
  "i32_trunc_sat_f64_u": {
    value: 3,
    mnemonic: "i32.trunc_sat_f64_u",
    stackBehavior: "PopPush",
    popOperands: ["Float64"] as const,
    pushOperands: ["Int32"] as const,
    prefix: 252,
    feature: "sat-trunc",
  },
  "i64_trunc_sat_f32_s": {
    value: 4,
    mnemonic: "i64.trunc_sat_f32_s",
    stackBehavior: "PopPush",
    popOperands: ["Float32"] as const,
    pushOperands: ["Int64"] as const,
    prefix: 252,
    feature: "sat-trunc",
  },
  "i64_trunc_sat_f32_u": {
    value: 5,
    mnemonic: "i64.trunc_sat_f32_u",
    stackBehavior: "PopPush",
    popOperands: ["Float32"] as const,
    pushOperands: ["Int64"] as const,
    prefix: 252,
    feature: "sat-trunc",
  },
  "i64_trunc_sat_f64_s": {
    value: 6,
    mnemonic: "i64.trunc_sat_f64_s",
    stackBehavior: "PopPush",
    popOperands: ["Float64"] as const,
    pushOperands: ["Int64"] as const,
    prefix: 252,
    feature: "sat-trunc",
  },
  "i64_trunc_sat_f64_u": {
    value: 7,
    mnemonic: "i64.trunc_sat_f64_u",
    stackBehavior: "PopPush",
    popOperands: ["Float64"] as const,
    pushOperands: ["Int64"] as const,
    prefix: 252,
    feature: "sat-trunc",
  },
  "memory_init": {
    value: 8,
    mnemonic: "memory.init",
    immediate: "MemoryImmediate",
    stackBehavior: "Pop",
    popOperands: ["Int32","Int32","Int32"] as const,
    prefix: 252,
    feature: "bulk-memory",
  },
  "data_drop": {
    value: 9,
    mnemonic: "data.drop",
    immediate: "VarUInt32",
    stackBehavior: "None",
    prefix: 252,
    feature: "bulk-memory",
  },
  "memory_copy": {
    value: 10,
    mnemonic: "memory.copy",
    immediate: "MemoryImmediate",
    stackBehavior: "Pop",
    popOperands: ["Int32","Int32","Int32"] as const,
    prefix: 252,
    feature: "bulk-memory",
  },
  "memory_fill": {
    value: 11,
    mnemonic: "memory.fill",
    immediate: "VarUInt1",
    stackBehavior: "Pop",
    popOperands: ["Int32","Int32","Int32"] as const,
    prefix: 252,
    feature: "bulk-memory",
  },
  "table_init": {
    value: 12,
    mnemonic: "table.init",
    immediate: "MemoryImmediate",
    stackBehavior: "Pop",
    popOperands: ["Int32","Int32","Int32"] as const,
    prefix: 252,
    feature: "bulk-memory",
  },
  "elem_drop": {
    value: 13,
    mnemonic: "elem.drop",
    immediate: "VarUInt32",
    stackBehavior: "None",
    prefix: 252,
    feature: "bulk-memory",
  },
  "table_copy": {
    value: 14,
    mnemonic: "table.copy",
    immediate: "MemoryImmediate",
    stackBehavior: "Pop",
    popOperands: ["Int32","Int32","Int32"] as const,
    prefix: 252,
    feature: "bulk-memory",
  },
  "table_grow": {
    value: 15,
    mnemonic: "table.grow",
    immediate: "VarUInt32",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int32"] as const,
    pushOperands: ["Int32"] as const,
    prefix: 252,
    feature: "reference-types",
  },
  "table_size": {
    value: 16,
    mnemonic: "table.size",
    immediate: "VarUInt32",
    stackBehavior: "Push",
    pushOperands: ["Int32"] as const,
    prefix: 252,
    feature: "reference-types",
  },
  "table_fill": {
    value: 17,
    mnemonic: "table.fill",
    immediate: "VarUInt32",
    stackBehavior: "Pop",
    popOperands: ["Int32","Int32","Int32"] as const,
    prefix: 252,
    feature: "reference-types",
  },
  "ref_null": {
    value: 208,
    mnemonic: "ref.null",
    immediate: "VarUInt32",
    stackBehavior: "Push",
    pushOperands: ["Int32"] as const,
    feature: "reference-types",
  },
  "ref_is_null": {
    value: 209,
    mnemonic: "ref.is_null",
    stackBehavior: "PopPush",
    popOperands: ["Int32"] as const,
    pushOperands: ["Int32"] as const,
    feature: "reference-types",
  },
  "ref_func": {
    value: 210,
    mnemonic: "ref.func",
    immediate: "Function",
    stackBehavior: "Push",
    pushOperands: ["Int32"] as const,
    feature: "reference-types",
  },
  "table_get": {
    value: 37,
    mnemonic: "table.get",
    immediate: "VarUInt32",
    stackBehavior: "PopPush",
    popOperands: ["Int32"] as const,
    pushOperands: ["Int32"] as const,
    feature: "reference-types",
  },
  "table_set": {
    value: 38,
    mnemonic: "table.set",
    immediate: "VarUInt32",
    stackBehavior: "Pop",
    popOperands: ["Int32","Int32"] as const,
    feature: "reference-types",
  },
  "v128_load": {
    value: 0,
    mnemonic: "v128.load",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "v128_load8x8_s": {
    value: 1,
    mnemonic: "v128.load8x8_s",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "v128_load8x8_u": {
    value: 2,
    mnemonic: "v128.load8x8_u",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "v128_load16x4_s": {
    value: 3,
    mnemonic: "v128.load16x4_s",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "v128_load16x4_u": {
    value: 4,
    mnemonic: "v128.load16x4_u",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "v128_load32x2_s": {
    value: 5,
    mnemonic: "v128.load32x2_s",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "v128_load32x2_u": {
    value: 6,
    mnemonic: "v128.load32x2_u",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "v128_load8_splat": {
    value: 7,
    mnemonic: "v128.load8_splat",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "v128_load16_splat": {
    value: 8,
    mnemonic: "v128.load16_splat",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "v128_load32_splat": {
    value: 9,
    mnemonic: "v128.load32_splat",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "v128_load64_splat": {
    value: 10,
    mnemonic: "v128.load64_splat",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "v128_store": {
    value: 11,
    mnemonic: "v128.store",
    immediate: "MemoryImmediate",
    stackBehavior: "Pop",
    popOperands: ["Int32","V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "v128_const": {
    value: 12,
    mnemonic: "v128.const",
    immediate: "V128Const",
    stackBehavior: "Push",
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i8x16_shuffle": {
    value: 13,
    mnemonic: "i8x16.shuffle",
    immediate: "ShuffleMask",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i8x16_swizzle": {
    value: 14,
    mnemonic: "i8x16.swizzle",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i8x16_splat": {
    value: 15,
    mnemonic: "i8x16.splat",
    stackBehavior: "PopPush",
    popOperands: ["Int32"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i16x8_splat": {
    value: 16,
    mnemonic: "i16x8.splat",
    stackBehavior: "PopPush",
    popOperands: ["Int32"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i32x4_splat": {
    value: 17,
    mnemonic: "i32x4.splat",
    stackBehavior: "PopPush",
    popOperands: ["Int32"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i64x2_splat": {
    value: 18,
    mnemonic: "i64x2.splat",
    stackBehavior: "PopPush",
    popOperands: ["Int64"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "f32x4_splat": {
    value: 19,
    mnemonic: "f32x4.splat",
    stackBehavior: "PopPush",
    popOperands: ["Float32"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "f64x2_splat": {
    value: 20,
    mnemonic: "f64x2.splat",
    stackBehavior: "PopPush",
    popOperands: ["Float64"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i8x16_extract_lane_s": {
    value: 21,
    mnemonic: "i8x16.extract_lane_s",
    immediate: "LaneIndex",
    stackBehavior: "PopPush",
    popOperands: ["V128"] as const,
    pushOperands: ["Int32"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i8x16_extract_lane_u": {
    value: 22,
    mnemonic: "i8x16.extract_lane_u",
    immediate: "LaneIndex",
    stackBehavior: "PopPush",
    popOperands: ["V128"] as const,
    pushOperands: ["Int32"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i8x16_replace_lane": {
    value: 23,
    mnemonic: "i8x16.replace_lane",
    immediate: "LaneIndex",
    stackBehavior: "PopPush",
    popOperands: ["V128","Int32"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i16x8_extract_lane_s": {
    value: 24,
    mnemonic: "i16x8.extract_lane_s",
    immediate: "LaneIndex",
    stackBehavior: "PopPush",
    popOperands: ["V128"] as const,
    pushOperands: ["Int32"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i16x8_extract_lane_u": {
    value: 25,
    mnemonic: "i16x8.extract_lane_u",
    immediate: "LaneIndex",
    stackBehavior: "PopPush",
    popOperands: ["V128"] as const,
    pushOperands: ["Int32"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i16x8_replace_lane": {
    value: 26,
    mnemonic: "i16x8.replace_lane",
    immediate: "LaneIndex",
    stackBehavior: "PopPush",
    popOperands: ["V128","Int32"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i32x4_extract_lane": {
    value: 27,
    mnemonic: "i32x4.extract_lane",
    immediate: "LaneIndex",
    stackBehavior: "PopPush",
    popOperands: ["V128"] as const,
    pushOperands: ["Int32"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i32x4_replace_lane": {
    value: 28,
    mnemonic: "i32x4.replace_lane",
    immediate: "LaneIndex",
    stackBehavior: "PopPush",
    popOperands: ["V128","Int32"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i64x2_extract_lane": {
    value: 29,
    mnemonic: "i64x2.extract_lane",
    immediate: "LaneIndex",
    stackBehavior: "PopPush",
    popOperands: ["V128"] as const,
    pushOperands: ["Int64"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i64x2_replace_lane": {
    value: 30,
    mnemonic: "i64x2.replace_lane",
    immediate: "LaneIndex",
    stackBehavior: "PopPush",
    popOperands: ["V128","Int64"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "f32x4_extract_lane": {
    value: 31,
    mnemonic: "f32x4.extract_lane",
    immediate: "LaneIndex",
    stackBehavior: "PopPush",
    popOperands: ["V128"] as const,
    pushOperands: ["Float32"] as const,
    prefix: 253,
    feature: "simd",
  },
  "f32x4_replace_lane": {
    value: 32,
    mnemonic: "f32x4.replace_lane",
    immediate: "LaneIndex",
    stackBehavior: "PopPush",
    popOperands: ["V128","Float32"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "f64x2_extract_lane": {
    value: 33,
    mnemonic: "f64x2.extract_lane",
    immediate: "LaneIndex",
    stackBehavior: "PopPush",
    popOperands: ["V128"] as const,
    pushOperands: ["Float64"] as const,
    prefix: 253,
    feature: "simd",
  },
  "f64x2_replace_lane": {
    value: 34,
    mnemonic: "f64x2.replace_lane",
    immediate: "LaneIndex",
    stackBehavior: "PopPush",
    popOperands: ["V128","Float64"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i8x16_eq": {
    value: 35,
    mnemonic: "i8x16.eq",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i8x16_ne": {
    value: 36,
    mnemonic: "i8x16.ne",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i8x16_lt_s": {
    value: 37,
    mnemonic: "i8x16.lt_s",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i8x16_lt_u": {
    value: 38,
    mnemonic: "i8x16.lt_u",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i8x16_gt_s": {
    value: 39,
    mnemonic: "i8x16.gt_s",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i8x16_gt_u": {
    value: 40,
    mnemonic: "i8x16.gt_u",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i8x16_le_s": {
    value: 41,
    mnemonic: "i8x16.le_s",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i8x16_le_u": {
    value: 42,
    mnemonic: "i8x16.le_u",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i8x16_ge_s": {
    value: 43,
    mnemonic: "i8x16.ge_s",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i8x16_ge_u": {
    value: 44,
    mnemonic: "i8x16.ge_u",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i16x8_eq": {
    value: 45,
    mnemonic: "i16x8.eq",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i16x8_ne": {
    value: 46,
    mnemonic: "i16x8.ne",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i16x8_lt_s": {
    value: 47,
    mnemonic: "i16x8.lt_s",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i16x8_lt_u": {
    value: 48,
    mnemonic: "i16x8.lt_u",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i16x8_gt_s": {
    value: 49,
    mnemonic: "i16x8.gt_s",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i16x8_gt_u": {
    value: 50,
    mnemonic: "i16x8.gt_u",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i16x8_le_s": {
    value: 51,
    mnemonic: "i16x8.le_s",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i16x8_le_u": {
    value: 52,
    mnemonic: "i16x8.le_u",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i16x8_ge_s": {
    value: 53,
    mnemonic: "i16x8.ge_s",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i16x8_ge_u": {
    value: 54,
    mnemonic: "i16x8.ge_u",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i32x4_eq": {
    value: 55,
    mnemonic: "i32x4.eq",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i32x4_ne": {
    value: 56,
    mnemonic: "i32x4.ne",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i32x4_lt_s": {
    value: 57,
    mnemonic: "i32x4.lt_s",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i32x4_lt_u": {
    value: 58,
    mnemonic: "i32x4.lt_u",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i32x4_gt_s": {
    value: 59,
    mnemonic: "i32x4.gt_s",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i32x4_gt_u": {
    value: 60,
    mnemonic: "i32x4.gt_u",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i32x4_le_s": {
    value: 61,
    mnemonic: "i32x4.le_s",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i32x4_le_u": {
    value: 62,
    mnemonic: "i32x4.le_u",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i32x4_ge_s": {
    value: 63,
    mnemonic: "i32x4.ge_s",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i32x4_ge_u": {
    value: 64,
    mnemonic: "i32x4.ge_u",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "f32x4_eq": {
    value: 65,
    mnemonic: "f32x4.eq",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "f32x4_ne": {
    value: 66,
    mnemonic: "f32x4.ne",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "f32x4_lt": {
    value: 67,
    mnemonic: "f32x4.lt",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "f32x4_gt": {
    value: 68,
    mnemonic: "f32x4.gt",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "f32x4_le": {
    value: 69,
    mnemonic: "f32x4.le",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "f32x4_ge": {
    value: 70,
    mnemonic: "f32x4.ge",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "f64x2_eq": {
    value: 71,
    mnemonic: "f64x2.eq",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "f64x2_ne": {
    value: 72,
    mnemonic: "f64x2.ne",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "f64x2_lt": {
    value: 73,
    mnemonic: "f64x2.lt",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "f64x2_gt": {
    value: 74,
    mnemonic: "f64x2.gt",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "f64x2_le": {
    value: 75,
    mnemonic: "f64x2.le",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "f64x2_ge": {
    value: 76,
    mnemonic: "f64x2.ge",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "v128_not": {
    value: 77,
    mnemonic: "v128.not",
    stackBehavior: "PopPush",
    popOperands: ["V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "v128_and": {
    value: 78,
    mnemonic: "v128.and",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "v128_andnot": {
    value: 79,
    mnemonic: "v128.andnot",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "v128_or": {
    value: 80,
    mnemonic: "v128.or",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "v128_xor": {
    value: 81,
    mnemonic: "v128.xor",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "v128_bitselect": {
    value: 82,
    mnemonic: "v128.bitselect",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "v128_any_true": {
    value: 83,
    mnemonic: "v128.any_true",
    stackBehavior: "PopPush",
    popOperands: ["V128"] as const,
    pushOperands: ["Int32"] as const,
    prefix: 253,
    feature: "simd",
  },
  "v128_load8_lane": {
    value: 84,
    mnemonic: "v128.load8_lane",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "v128_load16_lane": {
    value: 85,
    mnemonic: "v128.load16_lane",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "v128_load32_lane": {
    value: 86,
    mnemonic: "v128.load32_lane",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "v128_load64_lane": {
    value: 87,
    mnemonic: "v128.load64_lane",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "v128_store8_lane": {
    value: 88,
    mnemonic: "v128.store8_lane",
    immediate: "MemoryImmediate",
    stackBehavior: "Pop",
    popOperands: ["Int32","V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "v128_store16_lane": {
    value: 89,
    mnemonic: "v128.store16_lane",
    immediate: "MemoryImmediate",
    stackBehavior: "Pop",
    popOperands: ["Int32","V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "v128_store32_lane": {
    value: 90,
    mnemonic: "v128.store32_lane",
    immediate: "MemoryImmediate",
    stackBehavior: "Pop",
    popOperands: ["Int32","V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "v128_store64_lane": {
    value: 91,
    mnemonic: "v128.store64_lane",
    immediate: "MemoryImmediate",
    stackBehavior: "Pop",
    popOperands: ["Int32","V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "v128_load32_zero": {
    value: 92,
    mnemonic: "v128.load32_zero",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "v128_load64_zero": {
    value: 93,
    mnemonic: "v128.load64_zero",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i32x4_trunc_sat_f32x4_s": {
    value: 94,
    mnemonic: "i32x4.trunc_sat_f32x4_s",
    stackBehavior: "PopPush",
    popOperands: ["V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i32x4_trunc_sat_f32x4_u": {
    value: 95,
    mnemonic: "i32x4.trunc_sat_f32x4_u",
    stackBehavior: "PopPush",
    popOperands: ["V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i8x16_abs": {
    value: 96,
    mnemonic: "i8x16.abs",
    stackBehavior: "PopPush",
    popOperands: ["V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i8x16_neg": {
    value: 97,
    mnemonic: "i8x16.neg",
    stackBehavior: "PopPush",
    popOperands: ["V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i8x16_popcnt": {
    value: 98,
    mnemonic: "i8x16.popcnt",
    stackBehavior: "PopPush",
    popOperands: ["V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i8x16_all_true": {
    value: 99,
    mnemonic: "i8x16.all_true",
    stackBehavior: "PopPush",
    popOperands: ["V128"] as const,
    pushOperands: ["Int32"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i8x16_bitmask": {
    value: 100,
    mnemonic: "i8x16.bitmask",
    stackBehavior: "PopPush",
    popOperands: ["V128"] as const,
    pushOperands: ["Int32"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i8x16_narrow_i16x8_s": {
    value: 101,
    mnemonic: "i8x16.narrow_i16x8_s",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i8x16_narrow_i16x8_u": {
    value: 102,
    mnemonic: "i8x16.narrow_i16x8_u",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "f32x4_ceil": {
    value: 103,
    mnemonic: "f32x4.ceil",
    stackBehavior: "PopPush",
    popOperands: ["V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "f32x4_floor": {
    value: 104,
    mnemonic: "f32x4.floor",
    stackBehavior: "PopPush",
    popOperands: ["V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "f32x4_trunc": {
    value: 105,
    mnemonic: "f32x4.trunc",
    stackBehavior: "PopPush",
    popOperands: ["V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "f32x4_nearest": {
    value: 106,
    mnemonic: "f32x4.nearest",
    stackBehavior: "PopPush",
    popOperands: ["V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i8x16_shl": {
    value: 107,
    mnemonic: "i8x16.shl",
    stackBehavior: "PopPush",
    popOperands: ["V128","Int32"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i8x16_shr_s": {
    value: 108,
    mnemonic: "i8x16.shr_s",
    stackBehavior: "PopPush",
    popOperands: ["V128","Int32"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i8x16_shr_u": {
    value: 109,
    mnemonic: "i8x16.shr_u",
    stackBehavior: "PopPush",
    popOperands: ["V128","Int32"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i8x16_add": {
    value: 110,
    mnemonic: "i8x16.add",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i8x16_add_sat_s": {
    value: 111,
    mnemonic: "i8x16.add_sat_s",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i8x16_add_sat_u": {
    value: 112,
    mnemonic: "i8x16.add_sat_u",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i8x16_sub": {
    value: 113,
    mnemonic: "i8x16.sub",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i8x16_sub_sat_s": {
    value: 114,
    mnemonic: "i8x16.sub_sat_s",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i8x16_sub_sat_u": {
    value: 115,
    mnemonic: "i8x16.sub_sat_u",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "f64x2_ceil": {
    value: 116,
    mnemonic: "f64x2.ceil",
    stackBehavior: "PopPush",
    popOperands: ["V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "f64x2_floor": {
    value: 117,
    mnemonic: "f64x2.floor",
    stackBehavior: "PopPush",
    popOperands: ["V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i8x16_min_s": {
    value: 118,
    mnemonic: "i8x16.min_s",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i8x16_min_u": {
    value: 119,
    mnemonic: "i8x16.min_u",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i8x16_max_s": {
    value: 120,
    mnemonic: "i8x16.max_s",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i8x16_max_u": {
    value: 121,
    mnemonic: "i8x16.max_u",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "f64x2_trunc": {
    value: 122,
    mnemonic: "f64x2.trunc",
    stackBehavior: "PopPush",
    popOperands: ["V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i8x16_avgr_u": {
    value: 123,
    mnemonic: "i8x16.avgr_u",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i16x8_extadd_pairwise_i8x16_s": {
    value: 124,
    mnemonic: "i16x8.extadd_pairwise_i8x16_s",
    stackBehavior: "PopPush",
    popOperands: ["V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i16x8_extadd_pairwise_i8x16_u": {
    value: 125,
    mnemonic: "i16x8.extadd_pairwise_i8x16_u",
    stackBehavior: "PopPush",
    popOperands: ["V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i32x4_extadd_pairwise_i16x8_s": {
    value: 126,
    mnemonic: "i32x4.extadd_pairwise_i16x8_s",
    stackBehavior: "PopPush",
    popOperands: ["V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i32x4_extadd_pairwise_i16x8_u": {
    value: 127,
    mnemonic: "i32x4.extadd_pairwise_i16x8_u",
    stackBehavior: "PopPush",
    popOperands: ["V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i16x8_abs": {
    value: 128,
    mnemonic: "i16x8.abs",
    stackBehavior: "PopPush",
    popOperands: ["V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i16x8_neg": {
    value: 129,
    mnemonic: "i16x8.neg",
    stackBehavior: "PopPush",
    popOperands: ["V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i16x8_q15mulr_sat_s": {
    value: 130,
    mnemonic: "i16x8.q15mulr_sat_s",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i16x8_all_true": {
    value: 131,
    mnemonic: "i16x8.all_true",
    stackBehavior: "PopPush",
    popOperands: ["V128"] as const,
    pushOperands: ["Int32"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i16x8_bitmask": {
    value: 132,
    mnemonic: "i16x8.bitmask",
    stackBehavior: "PopPush",
    popOperands: ["V128"] as const,
    pushOperands: ["Int32"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i16x8_narrow_i32x4_s": {
    value: 133,
    mnemonic: "i16x8.narrow_i32x4_s",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i16x8_narrow_i32x4_u": {
    value: 134,
    mnemonic: "i16x8.narrow_i32x4_u",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i16x8_extend_low_i8x16_s": {
    value: 135,
    mnemonic: "i16x8.extend_low_i8x16_s",
    stackBehavior: "PopPush",
    popOperands: ["V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i16x8_extend_high_i8x16_s": {
    value: 136,
    mnemonic: "i16x8.extend_high_i8x16_s",
    stackBehavior: "PopPush",
    popOperands: ["V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i16x8_extend_low_i8x16_u": {
    value: 137,
    mnemonic: "i16x8.extend_low_i8x16_u",
    stackBehavior: "PopPush",
    popOperands: ["V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i16x8_extend_high_i8x16_u": {
    value: 138,
    mnemonic: "i16x8.extend_high_i8x16_u",
    stackBehavior: "PopPush",
    popOperands: ["V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i16x8_shl": {
    value: 139,
    mnemonic: "i16x8.shl",
    stackBehavior: "PopPush",
    popOperands: ["V128","Int32"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i16x8_shr_s": {
    value: 140,
    mnemonic: "i16x8.shr_s",
    stackBehavior: "PopPush",
    popOperands: ["V128","Int32"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i16x8_shr_u": {
    value: 141,
    mnemonic: "i16x8.shr_u",
    stackBehavior: "PopPush",
    popOperands: ["V128","Int32"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i16x8_add": {
    value: 142,
    mnemonic: "i16x8.add",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i16x8_add_sat_s": {
    value: 143,
    mnemonic: "i16x8.add_sat_s",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i16x8_add_sat_u": {
    value: 144,
    mnemonic: "i16x8.add_sat_u",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i16x8_sub": {
    value: 145,
    mnemonic: "i16x8.sub",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i16x8_sub_sat_s": {
    value: 146,
    mnemonic: "i16x8.sub_sat_s",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i16x8_sub_sat_u": {
    value: 147,
    mnemonic: "i16x8.sub_sat_u",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "f64x2_nearest": {
    value: 148,
    mnemonic: "f64x2.nearest",
    stackBehavior: "PopPush",
    popOperands: ["V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i16x8_mul": {
    value: 149,
    mnemonic: "i16x8.mul",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i16x8_min_s": {
    value: 150,
    mnemonic: "i16x8.min_s",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i16x8_min_u": {
    value: 151,
    mnemonic: "i16x8.min_u",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i16x8_max_s": {
    value: 152,
    mnemonic: "i16x8.max_s",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i16x8_max_u": {
    value: 153,
    mnemonic: "i16x8.max_u",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i16x8_avgr_u": {
    value: 155,
    mnemonic: "i16x8.avgr_u",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i16x8_extmul_low_i8x16_s": {
    value: 156,
    mnemonic: "i16x8.extmul_low_i8x16_s",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i16x8_extmul_high_i8x16_s": {
    value: 157,
    mnemonic: "i16x8.extmul_high_i8x16_s",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i16x8_extmul_low_i8x16_u": {
    value: 158,
    mnemonic: "i16x8.extmul_low_i8x16_u",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i16x8_extmul_high_i8x16_u": {
    value: 159,
    mnemonic: "i16x8.extmul_high_i8x16_u",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i32x4_abs": {
    value: 160,
    mnemonic: "i32x4.abs",
    stackBehavior: "PopPush",
    popOperands: ["V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i32x4_neg": {
    value: 161,
    mnemonic: "i32x4.neg",
    stackBehavior: "PopPush",
    popOperands: ["V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i32x4_all_true": {
    value: 163,
    mnemonic: "i32x4.all_true",
    stackBehavior: "PopPush",
    popOperands: ["V128"] as const,
    pushOperands: ["Int32"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i32x4_bitmask": {
    value: 164,
    mnemonic: "i32x4.bitmask",
    stackBehavior: "PopPush",
    popOperands: ["V128"] as const,
    pushOperands: ["Int32"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i32x4_extend_low_i16x8_s": {
    value: 167,
    mnemonic: "i32x4.extend_low_i16x8_s",
    stackBehavior: "PopPush",
    popOperands: ["V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i32x4_extend_high_i16x8_s": {
    value: 168,
    mnemonic: "i32x4.extend_high_i16x8_s",
    stackBehavior: "PopPush",
    popOperands: ["V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i32x4_extend_low_i16x8_u": {
    value: 169,
    mnemonic: "i32x4.extend_low_i16x8_u",
    stackBehavior: "PopPush",
    popOperands: ["V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i32x4_extend_high_i16x8_u": {
    value: 170,
    mnemonic: "i32x4.extend_high_i16x8_u",
    stackBehavior: "PopPush",
    popOperands: ["V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i32x4_shl": {
    value: 171,
    mnemonic: "i32x4.shl",
    stackBehavior: "PopPush",
    popOperands: ["V128","Int32"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i32x4_shr_s": {
    value: 172,
    mnemonic: "i32x4.shr_s",
    stackBehavior: "PopPush",
    popOperands: ["V128","Int32"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i32x4_shr_u": {
    value: 173,
    mnemonic: "i32x4.shr_u",
    stackBehavior: "PopPush",
    popOperands: ["V128","Int32"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i32x4_add": {
    value: 174,
    mnemonic: "i32x4.add",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "f32x4_convert_i32x4_s": {
    value: 175,
    mnemonic: "f32x4.convert_i32x4_s",
    stackBehavior: "PopPush",
    popOperands: ["V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "f32x4_convert_i32x4_u": {
    value: 176,
    mnemonic: "f32x4.convert_i32x4_u",
    stackBehavior: "PopPush",
    popOperands: ["V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i32x4_sub": {
    value: 177,
    mnemonic: "i32x4.sub",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i32x4_mul": {
    value: 181,
    mnemonic: "i32x4.mul",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i32x4_min_s": {
    value: 182,
    mnemonic: "i32x4.min_s",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i32x4_min_u": {
    value: 183,
    mnemonic: "i32x4.min_u",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i32x4_max_s": {
    value: 184,
    mnemonic: "i32x4.max_s",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i32x4_max_u": {
    value: 185,
    mnemonic: "i32x4.max_u",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i32x4_dot_i16x8_s": {
    value: 186,
    mnemonic: "i32x4.dot_i16x8_s",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i32x4_extmul_low_i16x8_s": {
    value: 188,
    mnemonic: "i32x4.extmul_low_i16x8_s",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i32x4_extmul_high_i16x8_s": {
    value: 189,
    mnemonic: "i32x4.extmul_high_i16x8_s",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i32x4_extmul_low_i16x8_u": {
    value: 190,
    mnemonic: "i32x4.extmul_low_i16x8_u",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i32x4_extmul_high_i16x8_u": {
    value: 191,
    mnemonic: "i32x4.extmul_high_i16x8_u",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i64x2_abs": {
    value: 192,
    mnemonic: "i64x2.abs",
    stackBehavior: "PopPush",
    popOperands: ["V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i64x2_neg": {
    value: 193,
    mnemonic: "i64x2.neg",
    stackBehavior: "PopPush",
    popOperands: ["V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i64x2_all_true": {
    value: 195,
    mnemonic: "i64x2.all_true",
    stackBehavior: "PopPush",
    popOperands: ["V128"] as const,
    pushOperands: ["Int32"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i64x2_bitmask": {
    value: 196,
    mnemonic: "i64x2.bitmask",
    stackBehavior: "PopPush",
    popOperands: ["V128"] as const,
    pushOperands: ["Int32"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i64x2_extend_low_i32x4_s": {
    value: 199,
    mnemonic: "i64x2.extend_low_i32x4_s",
    stackBehavior: "PopPush",
    popOperands: ["V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i64x2_extend_high_i32x4_s": {
    value: 200,
    mnemonic: "i64x2.extend_high_i32x4_s",
    stackBehavior: "PopPush",
    popOperands: ["V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i64x2_extend_low_i32x4_u": {
    value: 201,
    mnemonic: "i64x2.extend_low_i32x4_u",
    stackBehavior: "PopPush",
    popOperands: ["V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i64x2_extend_high_i32x4_u": {
    value: 202,
    mnemonic: "i64x2.extend_high_i32x4_u",
    stackBehavior: "PopPush",
    popOperands: ["V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i64x2_shl": {
    value: 203,
    mnemonic: "i64x2.shl",
    stackBehavior: "PopPush",
    popOperands: ["V128","Int32"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i64x2_shr_s": {
    value: 204,
    mnemonic: "i64x2.shr_s",
    stackBehavior: "PopPush",
    popOperands: ["V128","Int32"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i64x2_shr_u": {
    value: 205,
    mnemonic: "i64x2.shr_u",
    stackBehavior: "PopPush",
    popOperands: ["V128","Int32"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i64x2_add": {
    value: 206,
    mnemonic: "i64x2.add",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i64x2_sub": {
    value: 209,
    mnemonic: "i64x2.sub",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i64x2_mul": {
    value: 213,
    mnemonic: "i64x2.mul",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i64x2_eq": {
    value: 214,
    mnemonic: "i64x2.eq",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i64x2_ne": {
    value: 215,
    mnemonic: "i64x2.ne",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i64x2_lt_s": {
    value: 216,
    mnemonic: "i64x2.lt_s",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i64x2_gt_s": {
    value: 217,
    mnemonic: "i64x2.gt_s",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i64x2_le_s": {
    value: 218,
    mnemonic: "i64x2.le_s",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i64x2_ge_s": {
    value: 219,
    mnemonic: "i64x2.ge_s",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i64x2_extmul_low_i32x4_s": {
    value: 220,
    mnemonic: "i64x2.extmul_low_i32x4_s",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i64x2_extmul_high_i32x4_s": {
    value: 221,
    mnemonic: "i64x2.extmul_high_i32x4_s",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i64x2_extmul_low_i32x4_u": {
    value: 222,
    mnemonic: "i64x2.extmul_low_i32x4_u",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i64x2_extmul_high_i32x4_u": {
    value: 223,
    mnemonic: "i64x2.extmul_high_i32x4_u",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "f32x4_abs": {
    value: 224,
    mnemonic: "f32x4.abs",
    stackBehavior: "PopPush",
    popOperands: ["V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "f32x4_neg": {
    value: 225,
    mnemonic: "f32x4.neg",
    stackBehavior: "PopPush",
    popOperands: ["V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "f32x4_sqrt": {
    value: 227,
    mnemonic: "f32x4.sqrt",
    stackBehavior: "PopPush",
    popOperands: ["V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "f32x4_add": {
    value: 228,
    mnemonic: "f32x4.add",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "f32x4_sub": {
    value: 229,
    mnemonic: "f32x4.sub",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "f32x4_mul": {
    value: 230,
    mnemonic: "f32x4.mul",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "f32x4_div": {
    value: 231,
    mnemonic: "f32x4.div",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "f32x4_min": {
    value: 232,
    mnemonic: "f32x4.min",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "f32x4_max": {
    value: 233,
    mnemonic: "f32x4.max",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "f32x4_pmin": {
    value: 234,
    mnemonic: "f32x4.pmin",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "f32x4_pmax": {
    value: 235,
    mnemonic: "f32x4.pmax",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "f64x2_abs": {
    value: 236,
    mnemonic: "f64x2.abs",
    stackBehavior: "PopPush",
    popOperands: ["V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "f64x2_neg": {
    value: 237,
    mnemonic: "f64x2.neg",
    stackBehavior: "PopPush",
    popOperands: ["V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "f64x2_sqrt": {
    value: 239,
    mnemonic: "f64x2.sqrt",
    stackBehavior: "PopPush",
    popOperands: ["V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "f64x2_add": {
    value: 240,
    mnemonic: "f64x2.add",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "f64x2_sub": {
    value: 241,
    mnemonic: "f64x2.sub",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "f64x2_mul": {
    value: 242,
    mnemonic: "f64x2.mul",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "f64x2_div": {
    value: 243,
    mnemonic: "f64x2.div",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "f64x2_min": {
    value: 244,
    mnemonic: "f64x2.min",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "f64x2_max": {
    value: 245,
    mnemonic: "f64x2.max",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "f64x2_pmin": {
    value: 246,
    mnemonic: "f64x2.pmin",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "f64x2_pmax": {
    value: 247,
    mnemonic: "f64x2.pmax",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i32x4_trunc_sat_f64x2_s_zero": {
    value: 248,
    mnemonic: "i32x4.trunc_sat_f64x2_s_zero",
    stackBehavior: "PopPush",
    popOperands: ["V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "i32x4_trunc_sat_f64x2_u_zero": {
    value: 249,
    mnemonic: "i32x4.trunc_sat_f64x2_u_zero",
    stackBehavior: "PopPush",
    popOperands: ["V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "f64x2_convert_low_i32x4_s": {
    value: 250,
    mnemonic: "f64x2.convert_low_i32x4_s",
    stackBehavior: "PopPush",
    popOperands: ["V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "f64x2_convert_low_i32x4_u": {
    value: 251,
    mnemonic: "f64x2.convert_low_i32x4_u",
    stackBehavior: "PopPush",
    popOperands: ["V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "f32x4_demote_f64x2_zero": {
    value: 252,
    mnemonic: "f32x4.demote_f64x2_zero",
    stackBehavior: "PopPush",
    popOperands: ["V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "f64x2_promote_low_f32x4": {
    value: 253,
    mnemonic: "f64x2.promote_low_f32x4",
    stackBehavior: "PopPush",
    popOperands: ["V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "simd",
  },
  "memory_atomic_notify": {
    value: 0,
    mnemonic: "memory.atomic.notify",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int32"] as const,
    pushOperands: ["Int32"] as const,
    prefix: 254,
    feature: "threads",
  },
  "memory_atomic_wait32": {
    value: 1,
    mnemonic: "memory.atomic.wait32",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int32","Int64"] as const,
    pushOperands: ["Int32"] as const,
    prefix: 254,
    feature: "threads",
  },
  "memory_atomic_wait64": {
    value: 2,
    mnemonic: "memory.atomic.wait64",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int64","Int64"] as const,
    pushOperands: ["Int32"] as const,
    prefix: 254,
    feature: "threads",
  },
  "atomic_fence": {
    value: 3,
    mnemonic: "atomic.fence",
    immediate: "VarUInt1",
    stackBehavior: "None",
    prefix: 254,
    feature: "threads",
  },
  "i32_atomic_load": {
    value: 16,
    mnemonic: "i32.atomic.load",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32"] as const,
    pushOperands: ["Int32"] as const,
    prefix: 254,
    feature: "threads",
  },
  "i64_atomic_load": {
    value: 17,
    mnemonic: "i64.atomic.load",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32"] as const,
    pushOperands: ["Int64"] as const,
    prefix: 254,
    feature: "threads",
  },
  "i32_atomic_load8_u": {
    value: 18,
    mnemonic: "i32.atomic.load8_u",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32"] as const,
    pushOperands: ["Int32"] as const,
    prefix: 254,
    feature: "threads",
  },
  "i32_atomic_load16_u": {
    value: 19,
    mnemonic: "i32.atomic.load16_u",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32"] as const,
    pushOperands: ["Int32"] as const,
    prefix: 254,
    feature: "threads",
  },
  "i64_atomic_load8_u": {
    value: 20,
    mnemonic: "i64.atomic.load8_u",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32"] as const,
    pushOperands: ["Int64"] as const,
    prefix: 254,
    feature: "threads",
  },
  "i64_atomic_load16_u": {
    value: 21,
    mnemonic: "i64.atomic.load16_u",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32"] as const,
    pushOperands: ["Int64"] as const,
    prefix: 254,
    feature: "threads",
  },
  "i64_atomic_load32_u": {
    value: 22,
    mnemonic: "i64.atomic.load32_u",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32"] as const,
    pushOperands: ["Int64"] as const,
    prefix: 254,
    feature: "threads",
  },
  "i32_atomic_store": {
    value: 23,
    mnemonic: "i32.atomic.store",
    immediate: "MemoryImmediate",
    stackBehavior: "Pop",
    popOperands: ["Int32","Int32"] as const,
    prefix: 254,
    feature: "threads",
  },
  "i64_atomic_store": {
    value: 24,
    mnemonic: "i64.atomic.store",
    immediate: "MemoryImmediate",
    stackBehavior: "Pop",
    popOperands: ["Int32","Int64"] as const,
    prefix: 254,
    feature: "threads",
  },
  "i32_atomic_store8": {
    value: 25,
    mnemonic: "i32.atomic.store8",
    immediate: "MemoryImmediate",
    stackBehavior: "Pop",
    popOperands: ["Int32","Int32"] as const,
    prefix: 254,
    feature: "threads",
  },
  "i32_atomic_store16": {
    value: 26,
    mnemonic: "i32.atomic.store16",
    immediate: "MemoryImmediate",
    stackBehavior: "Pop",
    popOperands: ["Int32","Int32"] as const,
    prefix: 254,
    feature: "threads",
  },
  "i64_atomic_store8": {
    value: 27,
    mnemonic: "i64.atomic.store8",
    immediate: "MemoryImmediate",
    stackBehavior: "Pop",
    popOperands: ["Int32","Int64"] as const,
    prefix: 254,
    feature: "threads",
  },
  "i64_atomic_store16": {
    value: 28,
    mnemonic: "i64.atomic.store16",
    immediate: "MemoryImmediate",
    stackBehavior: "Pop",
    popOperands: ["Int32","Int64"] as const,
    prefix: 254,
    feature: "threads",
  },
  "i64_atomic_store32": {
    value: 29,
    mnemonic: "i64.atomic.store32",
    immediate: "MemoryImmediate",
    stackBehavior: "Pop",
    popOperands: ["Int32","Int64"] as const,
    prefix: 254,
    feature: "threads",
  },
  "i32_atomic_rmw_add": {
    value: 30,
    mnemonic: "i32.atomic.rmw.add",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int32"] as const,
    pushOperands: ["Int32"] as const,
    prefix: 254,
    feature: "threads",
  },
  "i64_atomic_rmw_add": {
    value: 31,
    mnemonic: "i64.atomic.rmw.add",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int64"] as const,
    pushOperands: ["Int64"] as const,
    prefix: 254,
    feature: "threads",
  },
  "i32_atomic_rmw8_add_u": {
    value: 32,
    mnemonic: "i32.atomic.rmw8.add_u",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int32"] as const,
    pushOperands: ["Int32"] as const,
    prefix: 254,
    feature: "threads",
  },
  "i32_atomic_rmw16_add_u": {
    value: 33,
    mnemonic: "i32.atomic.rmw16.add_u",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int32"] as const,
    pushOperands: ["Int32"] as const,
    prefix: 254,
    feature: "threads",
  },
  "i64_atomic_rmw8_add_u": {
    value: 34,
    mnemonic: "i64.atomic.rmw8.add_u",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int64"] as const,
    pushOperands: ["Int64"] as const,
    prefix: 254,
    feature: "threads",
  },
  "i64_atomic_rmw16_add_u": {
    value: 35,
    mnemonic: "i64.atomic.rmw16.add_u",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int64"] as const,
    pushOperands: ["Int64"] as const,
    prefix: 254,
    feature: "threads",
  },
  "i64_atomic_rmw32_add_u": {
    value: 36,
    mnemonic: "i64.atomic.rmw32.add_u",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int64"] as const,
    pushOperands: ["Int64"] as const,
    prefix: 254,
    feature: "threads",
  },
  "i32_atomic_rmw_sub": {
    value: 37,
    mnemonic: "i32.atomic.rmw.sub",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int32"] as const,
    pushOperands: ["Int32"] as const,
    prefix: 254,
    feature: "threads",
  },
  "i64_atomic_rmw_sub": {
    value: 38,
    mnemonic: "i64.atomic.rmw.sub",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int64"] as const,
    pushOperands: ["Int64"] as const,
    prefix: 254,
    feature: "threads",
  },
  "i32_atomic_rmw8_sub_u": {
    value: 39,
    mnemonic: "i32.atomic.rmw8.sub_u",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int32"] as const,
    pushOperands: ["Int32"] as const,
    prefix: 254,
    feature: "threads",
  },
  "i32_atomic_rmw16_sub_u": {
    value: 40,
    mnemonic: "i32.atomic.rmw16.sub_u",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int32"] as const,
    pushOperands: ["Int32"] as const,
    prefix: 254,
    feature: "threads",
  },
  "i64_atomic_rmw8_sub_u": {
    value: 41,
    mnemonic: "i64.atomic.rmw8.sub_u",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int64"] as const,
    pushOperands: ["Int64"] as const,
    prefix: 254,
    feature: "threads",
  },
  "i64_atomic_rmw16_sub_u": {
    value: 42,
    mnemonic: "i64.atomic.rmw16.sub_u",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int64"] as const,
    pushOperands: ["Int64"] as const,
    prefix: 254,
    feature: "threads",
  },
  "i64_atomic_rmw32_sub_u": {
    value: 43,
    mnemonic: "i64.atomic.rmw32.sub_u",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int64"] as const,
    pushOperands: ["Int64"] as const,
    prefix: 254,
    feature: "threads",
  },
  "i32_atomic_rmw_and": {
    value: 44,
    mnemonic: "i32.atomic.rmw.and",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int32"] as const,
    pushOperands: ["Int32"] as const,
    prefix: 254,
    feature: "threads",
  },
  "i64_atomic_rmw_and": {
    value: 45,
    mnemonic: "i64.atomic.rmw.and",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int64"] as const,
    pushOperands: ["Int64"] as const,
    prefix: 254,
    feature: "threads",
  },
  "i32_atomic_rmw8_and_u": {
    value: 46,
    mnemonic: "i32.atomic.rmw8.and_u",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int32"] as const,
    pushOperands: ["Int32"] as const,
    prefix: 254,
    feature: "threads",
  },
  "i32_atomic_rmw16_and_u": {
    value: 47,
    mnemonic: "i32.atomic.rmw16.and_u",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int32"] as const,
    pushOperands: ["Int32"] as const,
    prefix: 254,
    feature: "threads",
  },
  "i64_atomic_rmw8_and_u": {
    value: 48,
    mnemonic: "i64.atomic.rmw8.and_u",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int64"] as const,
    pushOperands: ["Int64"] as const,
    prefix: 254,
    feature: "threads",
  },
  "i64_atomic_rmw16_and_u": {
    value: 49,
    mnemonic: "i64.atomic.rmw16.and_u",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int64"] as const,
    pushOperands: ["Int64"] as const,
    prefix: 254,
    feature: "threads",
  },
  "i64_atomic_rmw32_and_u": {
    value: 50,
    mnemonic: "i64.atomic.rmw32.and_u",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int64"] as const,
    pushOperands: ["Int64"] as const,
    prefix: 254,
    feature: "threads",
  },
  "i32_atomic_rmw_or": {
    value: 51,
    mnemonic: "i32.atomic.rmw.or",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int32"] as const,
    pushOperands: ["Int32"] as const,
    prefix: 254,
    feature: "threads",
  },
  "i64_atomic_rmw_or": {
    value: 52,
    mnemonic: "i64.atomic.rmw.or",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int64"] as const,
    pushOperands: ["Int64"] as const,
    prefix: 254,
    feature: "threads",
  },
  "i32_atomic_rmw8_or_u": {
    value: 53,
    mnemonic: "i32.atomic.rmw8.or_u",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int32"] as const,
    pushOperands: ["Int32"] as const,
    prefix: 254,
    feature: "threads",
  },
  "i32_atomic_rmw16_or_u": {
    value: 54,
    mnemonic: "i32.atomic.rmw16.or_u",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int32"] as const,
    pushOperands: ["Int32"] as const,
    prefix: 254,
    feature: "threads",
  },
  "i64_atomic_rmw8_or_u": {
    value: 55,
    mnemonic: "i64.atomic.rmw8.or_u",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int64"] as const,
    pushOperands: ["Int64"] as const,
    prefix: 254,
    feature: "threads",
  },
  "i64_atomic_rmw16_or_u": {
    value: 56,
    mnemonic: "i64.atomic.rmw16.or_u",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int64"] as const,
    pushOperands: ["Int64"] as const,
    prefix: 254,
    feature: "threads",
  },
  "i64_atomic_rmw32_or_u": {
    value: 57,
    mnemonic: "i64.atomic.rmw32.or_u",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int64"] as const,
    pushOperands: ["Int64"] as const,
    prefix: 254,
    feature: "threads",
  },
  "i32_atomic_rmw_xor": {
    value: 58,
    mnemonic: "i32.atomic.rmw.xor",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int32"] as const,
    pushOperands: ["Int32"] as const,
    prefix: 254,
    feature: "threads",
  },
  "i64_atomic_rmw_xor": {
    value: 59,
    mnemonic: "i64.atomic.rmw.xor",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int64"] as const,
    pushOperands: ["Int64"] as const,
    prefix: 254,
    feature: "threads",
  },
  "i32_atomic_rmw8_xor_u": {
    value: 60,
    mnemonic: "i32.atomic.rmw8.xor_u",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int32"] as const,
    pushOperands: ["Int32"] as const,
    prefix: 254,
    feature: "threads",
  },
  "i32_atomic_rmw16_xor_u": {
    value: 61,
    mnemonic: "i32.atomic.rmw16.xor_u",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int32"] as const,
    pushOperands: ["Int32"] as const,
    prefix: 254,
    feature: "threads",
  },
  "i64_atomic_rmw8_xor_u": {
    value: 62,
    mnemonic: "i64.atomic.rmw8.xor_u",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int64"] as const,
    pushOperands: ["Int64"] as const,
    prefix: 254,
    feature: "threads",
  },
  "i64_atomic_rmw16_xor_u": {
    value: 63,
    mnemonic: "i64.atomic.rmw16.xor_u",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int64"] as const,
    pushOperands: ["Int64"] as const,
    prefix: 254,
    feature: "threads",
  },
  "i64_atomic_rmw32_xor_u": {
    value: 64,
    mnemonic: "i64.atomic.rmw32.xor_u",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int64"] as const,
    pushOperands: ["Int64"] as const,
    prefix: 254,
    feature: "threads",
  },
  "i32_atomic_rmw_xchg": {
    value: 65,
    mnemonic: "i32.atomic.rmw.xchg",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int32"] as const,
    pushOperands: ["Int32"] as const,
    prefix: 254,
    feature: "threads",
  },
  "i64_atomic_rmw_xchg": {
    value: 66,
    mnemonic: "i64.atomic.rmw.xchg",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int64"] as const,
    pushOperands: ["Int64"] as const,
    prefix: 254,
    feature: "threads",
  },
  "i32_atomic_rmw8_xchg_u": {
    value: 67,
    mnemonic: "i32.atomic.rmw8.xchg_u",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int32"] as const,
    pushOperands: ["Int32"] as const,
    prefix: 254,
    feature: "threads",
  },
  "i32_atomic_rmw16_xchg_u": {
    value: 68,
    mnemonic: "i32.atomic.rmw16.xchg_u",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int32"] as const,
    pushOperands: ["Int32"] as const,
    prefix: 254,
    feature: "threads",
  },
  "i64_atomic_rmw8_xchg_u": {
    value: 69,
    mnemonic: "i64.atomic.rmw8.xchg_u",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int64"] as const,
    pushOperands: ["Int64"] as const,
    prefix: 254,
    feature: "threads",
  },
  "i64_atomic_rmw16_xchg_u": {
    value: 70,
    mnemonic: "i64.atomic.rmw16.xchg_u",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int64"] as const,
    pushOperands: ["Int64"] as const,
    prefix: 254,
    feature: "threads",
  },
  "i64_atomic_rmw32_xchg_u": {
    value: 71,
    mnemonic: "i64.atomic.rmw32.xchg_u",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int64"] as const,
    pushOperands: ["Int64"] as const,
    prefix: 254,
    feature: "threads",
  },
  "i32_atomic_rmw_cmpxchg": {
    value: 72,
    mnemonic: "i32.atomic.rmw.cmpxchg",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int32","Int32"] as const,
    pushOperands: ["Int32"] as const,
    prefix: 254,
    feature: "threads",
  },
  "i64_atomic_rmw_cmpxchg": {
    value: 73,
    mnemonic: "i64.atomic.rmw.cmpxchg",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int64","Int64"] as const,
    pushOperands: ["Int64"] as const,
    prefix: 254,
    feature: "threads",
  },
  "i32_atomic_rmw8_cmpxchg_u": {
    value: 74,
    mnemonic: "i32.atomic.rmw8.cmpxchg_u",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int32","Int32"] as const,
    pushOperands: ["Int32"] as const,
    prefix: 254,
    feature: "threads",
  },
  "i32_atomic_rmw16_cmpxchg_u": {
    value: 75,
    mnemonic: "i32.atomic.rmw16.cmpxchg_u",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int32","Int32"] as const,
    pushOperands: ["Int32"] as const,
    prefix: 254,
    feature: "threads",
  },
  "i64_atomic_rmw8_cmpxchg_u": {
    value: 76,
    mnemonic: "i64.atomic.rmw8.cmpxchg_u",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int64","Int64"] as const,
    pushOperands: ["Int64"] as const,
    prefix: 254,
    feature: "threads",
  },
  "i64_atomic_rmw16_cmpxchg_u": {
    value: 77,
    mnemonic: "i64.atomic.rmw16.cmpxchg_u",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int64","Int64"] as const,
    pushOperands: ["Int64"] as const,
    prefix: 254,
    feature: "threads",
  },
  "i64_atomic_rmw32_cmpxchg_u": {
    value: 78,
    mnemonic: "i64.atomic.rmw32.cmpxchg_u",
    immediate: "MemoryImmediate",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int64","Int64"] as const,
    pushOperands: ["Int64"] as const,
    prefix: 254,
    feature: "threads",
  },
  "i8x16_relaxed_swizzle": {
    value: 256,
    mnemonic: "i8x16.relaxed_swizzle",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "relaxed-simd",
  },
  "i32x4_relaxed_trunc_f32x4_s": {
    value: 257,
    mnemonic: "i32x4.relaxed_trunc_f32x4_s",
    stackBehavior: "PopPush",
    popOperands: ["V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "relaxed-simd",
  },
  "i32x4_relaxed_trunc_f32x4_u": {
    value: 258,
    mnemonic: "i32x4.relaxed_trunc_f32x4_u",
    stackBehavior: "PopPush",
    popOperands: ["V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "relaxed-simd",
  },
  "i32x4_relaxed_trunc_f64x2_s_zero": {
    value: 259,
    mnemonic: "i32x4.relaxed_trunc_f64x2_s_zero",
    stackBehavior: "PopPush",
    popOperands: ["V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "relaxed-simd",
  },
  "i32x4_relaxed_trunc_f64x2_u_zero": {
    value: 260,
    mnemonic: "i32x4.relaxed_trunc_f64x2_u_zero",
    stackBehavior: "PopPush",
    popOperands: ["V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "relaxed-simd",
  },
  "f32x4_relaxed_madd": {
    value: 261,
    mnemonic: "f32x4.relaxed_madd",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "relaxed-simd",
  },
  "f32x4_relaxed_nmadd": {
    value: 262,
    mnemonic: "f32x4.relaxed_nmadd",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "relaxed-simd",
  },
  "f64x2_relaxed_madd": {
    value: 263,
    mnemonic: "f64x2.relaxed_madd",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "relaxed-simd",
  },
  "f64x2_relaxed_nmadd": {
    value: 264,
    mnemonic: "f64x2.relaxed_nmadd",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "relaxed-simd",
  },
  "i8x16_relaxed_laneselect": {
    value: 265,
    mnemonic: "i8x16.relaxed_laneselect",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "relaxed-simd",
  },
  "i16x8_relaxed_laneselect": {
    value: 266,
    mnemonic: "i16x8.relaxed_laneselect",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "relaxed-simd",
  },
  "i32x4_relaxed_laneselect": {
    value: 267,
    mnemonic: "i32x4.relaxed_laneselect",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "relaxed-simd",
  },
  "i64x2_relaxed_laneselect": {
    value: 268,
    mnemonic: "i64x2.relaxed_laneselect",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "relaxed-simd",
  },
  "f32x4_relaxed_min": {
    value: 269,
    mnemonic: "f32x4.relaxed_min",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "relaxed-simd",
  },
  "f32x4_relaxed_max": {
    value: 270,
    mnemonic: "f32x4.relaxed_max",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "relaxed-simd",
  },
  "f64x2_relaxed_min": {
    value: 271,
    mnemonic: "f64x2.relaxed_min",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "relaxed-simd",
  },
  "f64x2_relaxed_max": {
    value: 272,
    mnemonic: "f64x2.relaxed_max",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "relaxed-simd",
  },
  "i16x8_relaxed_q15mulr_s": {
    value: 273,
    mnemonic: "i16x8.relaxed_q15mulr_s",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "relaxed-simd",
  },
  "i16x8_relaxed_dot_i8x16_i7x16_s": {
    value: 274,
    mnemonic: "i16x8.relaxed_dot_i8x16_i7x16_s",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "relaxed-simd",
  },
  "i32x4_relaxed_dot_i8x16_i7x16_add_s": {
    value: 275,
    mnemonic: "i32x4.relaxed_dot_i8x16_i7x16_add_s",
    stackBehavior: "PopPush",
    popOperands: ["V128","V128","V128"] as const,
    pushOperands: ["V128"] as const,
    prefix: 253,
    feature: "relaxed-simd",
  },
  "struct_new": {
    value: 0,
    mnemonic: "struct.new",
    immediate: "VarUInt32",
    stackBehavior: "Push",
    pushOperands: ["Any"] as const,
    prefix: 251,
    feature: "gc",
  },
  "struct_new_default": {
    value: 1,
    mnemonic: "struct.new_default",
    immediate: "VarUInt32",
    stackBehavior: "Push",
    pushOperands: ["Any"] as const,
    prefix: 251,
    feature: "gc",
  },
  "struct_get": {
    value: 2,
    mnemonic: "struct.get",
    immediate: "TypeIndexField",
    stackBehavior: "PopPush",
    popOperands: ["Any"] as const,
    pushOperands: ["Any"] as const,
    prefix: 251,
    feature: "gc",
  },
  "struct_get_s": {
    value: 3,
    mnemonic: "struct.get_s",
    immediate: "TypeIndexField",
    stackBehavior: "PopPush",
    popOperands: ["Any"] as const,
    pushOperands: ["Int32"] as const,
    prefix: 251,
    feature: "gc",
  },
  "struct_get_u": {
    value: 4,
    mnemonic: "struct.get_u",
    immediate: "TypeIndexField",
    stackBehavior: "PopPush",
    popOperands: ["Any"] as const,
    pushOperands: ["Int32"] as const,
    prefix: 251,
    feature: "gc",
  },
  "struct_set": {
    value: 5,
    mnemonic: "struct.set",
    immediate: "TypeIndexField",
    stackBehavior: "Pop",
    popOperands: ["Any","Any"] as const,
    prefix: 251,
    feature: "gc",
  },
  "array_new": {
    value: 6,
    mnemonic: "array.new",
    immediate: "VarUInt32",
    stackBehavior: "PopPush",
    popOperands: ["Any","Int32"] as const,
    pushOperands: ["Any"] as const,
    prefix: 251,
    feature: "gc",
  },
  "array_new_default": {
    value: 7,
    mnemonic: "array.new_default",
    immediate: "VarUInt32",
    stackBehavior: "PopPush",
    popOperands: ["Int32"] as const,
    pushOperands: ["Any"] as const,
    prefix: 251,
    feature: "gc",
  },
  "array_new_fixed": {
    value: 8,
    mnemonic: "array.new_fixed",
    immediate: "TypeIndexIndex",
    stackBehavior: "Push",
    pushOperands: ["Any"] as const,
    prefix: 251,
    feature: "gc",
  },
  "array_new_data": {
    value: 9,
    mnemonic: "array.new_data",
    immediate: "TypeIndexIndex",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int32"] as const,
    pushOperands: ["Any"] as const,
    prefix: 251,
    feature: "gc",
  },
  "array_new_elem": {
    value: 10,
    mnemonic: "array.new_elem",
    immediate: "TypeIndexIndex",
    stackBehavior: "PopPush",
    popOperands: ["Int32","Int32"] as const,
    pushOperands: ["Any"] as const,
    prefix: 251,
    feature: "gc",
  },
  "array_get": {
    value: 11,
    mnemonic: "array.get",
    immediate: "VarUInt32",
    stackBehavior: "PopPush",
    popOperands: ["Any","Int32"] as const,
    pushOperands: ["Any"] as const,
    prefix: 251,
    feature: "gc",
  },
  "array_get_s": {
    value: 12,
    mnemonic: "array.get_s",
    immediate: "VarUInt32",
    stackBehavior: "PopPush",
    popOperands: ["Any","Int32"] as const,
    pushOperands: ["Int32"] as const,
    prefix: 251,
    feature: "gc",
  },
  "array_get_u": {
    value: 13,
    mnemonic: "array.get_u",
    immediate: "VarUInt32",
    stackBehavior: "PopPush",
    popOperands: ["Any","Int32"] as const,
    pushOperands: ["Int32"] as const,
    prefix: 251,
    feature: "gc",
  },
  "array_set": {
    value: 14,
    mnemonic: "array.set",
    immediate: "VarUInt32",
    stackBehavior: "Pop",
    popOperands: ["Any","Int32","Any"] as const,
    prefix: 251,
    feature: "gc",
  },
  "array_len": {
    value: 15,
    mnemonic: "array.len",
    stackBehavior: "PopPush",
    popOperands: ["Any"] as const,
    pushOperands: ["Int32"] as const,
    prefix: 251,
    feature: "gc",
  },
  "array_fill": {
    value: 16,
    mnemonic: "array.fill",
    immediate: "VarUInt32",
    stackBehavior: "Pop",
    popOperands: ["Any","Int32","Any","Int32"] as const,
    prefix: 251,
    feature: "gc",
  },
  "array_copy": {
    value: 17,
    mnemonic: "array.copy",
    immediate: "TypeIndexIndex",
    stackBehavior: "Pop",
    popOperands: ["Any","Int32","Any","Int32","Int32"] as const,
    prefix: 251,
    feature: "gc",
  },
  "array_init_data": {
    value: 18,
    mnemonic: "array.init_data",
    immediate: "TypeIndexIndex",
    stackBehavior: "Pop",
    popOperands: ["Any","Int32","Int32","Int32"] as const,
    prefix: 251,
    feature: "gc",
  },
  "array_init_elem": {
    value: 19,
    mnemonic: "array.init_elem",
    immediate: "TypeIndexIndex",
    stackBehavior: "Pop",
    popOperands: ["Any","Int32","Int32","Int32"] as const,
    prefix: 251,
    feature: "gc",
  },
  "ref_test": {
    value: 20,
    mnemonic: "ref.test",
    immediate: "HeapType",
    stackBehavior: "PopPush",
    popOperands: ["Any"] as const,
    pushOperands: ["Int32"] as const,
    prefix: 251,
    feature: "gc",
  },
  "ref_test_null": {
    value: 21,
    mnemonic: "ref.test null",
    immediate: "HeapType",
    stackBehavior: "PopPush",
    popOperands: ["Any"] as const,
    pushOperands: ["Int32"] as const,
    prefix: 251,
    feature: "gc",
  },
  "ref_cast": {
    value: 22,
    mnemonic: "ref.cast",
    immediate: "HeapType",
    stackBehavior: "PopPush",
    popOperands: ["Any"] as const,
    pushOperands: ["Any"] as const,
    prefix: 251,
    feature: "gc",
  },
  "ref_cast_null": {
    value: 23,
    mnemonic: "ref.cast null",
    immediate: "HeapType",
    stackBehavior: "PopPush",
    popOperands: ["Any"] as const,
    pushOperands: ["Any"] as const,
    prefix: 251,
    feature: "gc",
  },
  "br_on_cast": {
    value: 24,
    mnemonic: "br_on_cast",
    immediate: "BrOnCast",
    stackBehavior: "PopPush",
    popOperands: ["Any"] as const,
    pushOperands: ["Any"] as const,
    prefix: 251,
    feature: "gc",
  },
  "br_on_cast_fail": {
    value: 25,
    mnemonic: "br_on_cast_fail",
    immediate: "BrOnCast",
    stackBehavior: "PopPush",
    popOperands: ["Any"] as const,
    pushOperands: ["Any"] as const,
    prefix: 251,
    feature: "gc",
  },
  "any_convert_extern": {
    value: 26,
    mnemonic: "any.convert_extern",
    stackBehavior: "PopPush",
    popOperands: ["Any"] as const,
    pushOperands: ["Any"] as const,
    prefix: 251,
    feature: "gc",
  },
  "extern_convert_any": {
    value: 27,
    mnemonic: "extern.convert_any",
    stackBehavior: "PopPush",
    popOperands: ["Any"] as const,
    pushOperands: ["Any"] as const,
    prefix: 251,
    feature: "gc",
  },
  "ref_i31": {
    value: 28,
    mnemonic: "ref.i31",
    stackBehavior: "PopPush",
    popOperands: ["Int32"] as const,
    pushOperands: ["Any"] as const,
    prefix: 251,
    feature: "gc",
  },
  "i31_get_s": {
    value: 29,
    mnemonic: "i31.get_s",
    stackBehavior: "PopPush",
    popOperands: ["Any"] as const,
    pushOperands: ["Int32"] as const,
    prefix: 251,
    feature: "gc",
  },
  "i31_get_u": {
    value: 30,
    mnemonic: "i31.get_u",
    stackBehavior: "PopPush",
    popOperands: ["Any"] as const,
    pushOperands: ["Int32"] as const,
    prefix: 251,
    feature: "gc",
  },
} as const satisfies Record<string, OpCodeDef>;

export default OpCodes;
